/**
 * @file contentManager.js
 * @description Manages loading and rendering of portfolio content from schema.json
 * @version 3.0.0 (Singleton Refactor)
 */

class ContentManager {
    constructor() {
        this.data = null; // This will hold GUI *manifest* data from schema.json
        this.terminalData = null; // This will hold terminal.json data
        this.isLoaded = false;
        this.templates = new Map();
        
        // NEW: Caches for on-demand content
        this.projectCache = new Map();
        this.blogCache = new Map();

        this.paths = {
            gui: './data/schema.json', // This is now the "manifest"
            terminal: './data/terminal.json'
        };
    }

    /**
     * Load *manifest* content from schema.json
     * @returns {Promise} - True if loaded successfully
     */
    async load() {
        try {
            const [guiResponse, terminalResponse] = await Promise.all([
                fetch(this.paths.gui),
                fetch(this.paths.terminal)
            ]);

            if (!guiResponse.ok) {
                throw new Error(`Failed to load GUI manifest: ${guiResponse.status}`);
            }
            if (!terminalResponse.ok) {
                throw new Error(`Failed to load Terminal content: ${terminalResponse.status}`);
            }

            this.data = await guiResponse.json(); // Store GUI manifest
            this.terminalData = await terminalResponse.json(); // Store Terminal data

            this.isLoaded = true;
            return true;

        } catch (error) {
            console.error('[ContentManager] Error loading manifests:', error);
            this.data = this.getFallbackData().gui; // Use fallback
            this.terminalData = this.getFallbackData().terminal; // Use fallback
            this.isLoaded = true;
            return false;
        }
    }

    /**
     * Alias for load() - for compatibility with core.js
     */
    async loadContent() {
        return await this.load();
    }

    /**
     * Get all content (for gui.js compatibility)
     * @returns {Object} - Full data object
     */
    getContent() {
        if (!this.isLoaded) {
            console.warn('[ContentManager] Data not loaded yet');
            return this.getFallbackData();
        }
        return this.data;
    }

    // --- Current Focus Getter ---
    getCurrentFocus() {
    if (this.isLoaded && this.data.current_focus) {
        return this.data.current_focus;
    }

    // Default Fallback
    return [
        { label: "BUILDING", value: "Wazuh SIEM.Detection Engineering", style: "accent" },
        {  "label": "FOCUS", "value": "Cloud Security Architecture", "style": "accent"  },
    ];
    }



    // --- PROFILE GETTER (Unchanged) ---
    /**
     * Get profile data (name, role, resume_url)
     * @returns {Object} - Profile object
     */
    getProfile() {
        if (!this.isLoaded) {
            console.warn('[ContentManager] Data not loaded yet');
            return { name: 'Error', role: 'Offline', resume_url: '#' };
        }
        if (!this.data.profile) {
            return {
                name: this.data.meta?.owner || 'Your Name',
                role: 'Systems Engineer',
                resume_url: '#'
            }
        }
        return this.data.profile;
    }

    /**
     * Get all projects *from the manifest*
     * @returns {Array} - Array of project objects
     */
    getProjects() {
        if (!this.isLoaded) {
            console.warn('[ContentManager] Data not loaded yet');
            return [];
        }
        return this.data.projects || [];
    }

    // --- MODIFIED: getProject(id) ---
    /**
     * Get single project by ID (fetches on-demand)
     * @param {string} id - Project ID
     * @returns {Promise<Object|null>} - Project object or null
     */
    async getProject(id) {
        // 1. Check cache first
        if (this.projectCache.has(id)) {
            return this.projectCache.get(id);
        }

        // 2. If not in cache, fetch it
        try {
            const response = await fetch(`./data/projects/${id}.json`);
            if (!response.ok) {
                throw new Error(`Project file not found: ${id}.json`);
            }
            const projectData = await response.json();
            
            // 3. Store in cache and return
            this.projectCache.set(id, projectData);
            return projectData;
        } catch (error) {
            console.error(`[ContentManager] Failed to fetch project ${id}:`, error);
            return null; // Return null on error
        }
    }

    /**
     * Get projects filtered by status
     * @param {string} status - Status to filter by (active, in-progress, planned)
     * @returns {Array} - Filtered projects
     */
    getProjectsByStatus(status) {
        return this.getProjects().filter(p => p.status === status);
    }

    /**
     * Get all blog posts *from the manifest*
     * @returns {Array} - Array of blog post objects
     */
    getBlogPosts() {
        if (!this.isLoaded) {
            console.warn('[ContentManager] Data not loaded yet');
            return [];
        }
        return this.data.blog || [];
    }
    
    // --- MODIFIED: getBlogPost(id) ---
    /**
     * Get single blog post by ID (fetches on-demand)
     * @param {string} id - Blog Post ID
     * @returns {Promise<Object|null>} - Blog post object or null
     */
    async getBlogPost(id) {
        // 1. Check cache first
        if (this.blogCache.has(id)) {
            return this.blogCache.get(id);
        }

        // 2. If not in cache, fetch it
        try {
            const response = await fetch(`./data/blog/${id}.json`);
            if (!response.ok) {
                throw new Error(`Blog file not found: ${id}.json`);
            }
            const postData = await response.json();
            
            // 3. Store in cache and return
            this.blogCache.set(id, postData);
            return postData;
        } catch (error) {
            console.error(`[ContentManager] Failed to fetch blog post ${id}:`, error);
            return null; // Return null on error
        }
    }

    /**
     * Get bio object
     * @returns {Object} - Bio object with paragraphs array
     */
    getBio() {
        if (!this.isLoaded) return { paragraphs: [] };
        return this.data.bio || { paragraphs: [] };
    }


    /**
     * Get skills object (tiered)
     * @returns {Object} - Skills object with current and developing tiers
     */
    getSkills() {
        if (!this.isLoaded) {
        return {
            current: { label: "Current Skills", description: "", items: [] },
            developing: { label: "Developing Skills", description: "", items: [] }
        };
        }
        return this.data.skills || {
        current: { label: "Current Skills", description: "", items: [] },
        developing: { label: "Developing Skills", description: "", items: [] }
        };
    }


    /**
     * Get contact information
     * @returns {Object} - Contact details
     */
    getContact() {
        if (!this.isLoaded) return {};
        return this.data.contact || {};
    }

    /**
     * Get experience array
     * @returns {Array} Array of experience objects
     */
    getExperience() {
        if (!this.isLoaded) return [];
        return this.data?.experience || [];
    }

    /**
     * Get certifications array
     * @returns {Array} Array of certification objects
     */
    getCertifications() {
        if (!this.isLoaded) return [];
        return this.data?.certifications || [];
    }

    /**
     * Get availability information
     * @returns {Object} - Availability object with interviews, start_date, timezone
     */
    getAvailability() {
        if (!this.isLoaded) return {};
        return this.data?.availability || {};
    }

    /**
     * Get system metadata (lastUpdated, version, etc.)
     * @returns {Object} - Metadata object
     */
    getMetadata() {
        if (!this.isLoaded) return { lastUpdated: 'Unknown', schemaVersion: '0.0' };
        return this.data?.metadata || { lastUpdated: 'Unknown', schemaVersion: '0.0' };
    }

    // --- Terminal Data Getters (Unchanged) ---

    /**
     * @returns {Object} The Virtual File System (VFS) object
     */
    getVFS() {
        if (!this.isLoaded) return {};
        return this.terminalData?.vfs || {};
    }

    /**
     * @returns {string} The HTML string for the 'help' command
     */
    getHelpText() {
        if (!this.isLoaded) return 'Help content not found.';
        return this.terminalData?.help || 'Help content not found.';
    }

    /**
     * @returns {Array} Array of boot log strings
     */
    getBootLogs() {
        if (!this.isLoaded) return ['[ERROR] Boot logs not found.'];
        return this.terminalData?.bootLogs || ['[ERROR] Boot logs not found.'];
    }

    /**
     * @returns {Array} Array of fortune strings
     */
    getFortunes() {
        if (!this.isLoaded) return ['Your fortune is... not found.'];
        return this.terminalData?.fortunes || ['Your fortune is... not found.'];
    }

    /**
     * Register a template function for rendering content
     * @param {string} type - Content type (projects, skills, etc.)
     * @param {Function} templateFn - Function that returns HTML string
     */
    registerTemplate(type, templateFn) {
        this.templates.set(type, templateFn);
    }

    /**
     * Render content using registered template
     * @param {string} type - Content type
     * @param {HTMLElement} container - DOM element to render into
     */
    render(type, container) {
        if (!this.templates.has(type)) {
            console.error(`[ContentManager] No template registered for: ${type}`);
            return;
        }

        const items = this.data[type] || [];
        const template = this.templates.get(type);
        const html = Array.isArray(items)
            ? items.map(item => template(item)).join('')
            : template(items);
        container.innerHTML = html;
    }

    /**
     * Fallback data if schema.json fails to load
     * @returns {Object} - Minimal content structure
     */
    getFallbackData() {
        return {
            gui: {
                profile: { 
                    name: "Offline Mode",
                    role: "Systems Engineer",
                    resume_url: "#"
                },
                projects: [
                    {
                        id: 'cyber-os-portfolio',
                        title: 'CYBER-OS Portfolio',
                        status: 'in-progress',
                        excerpt: 'Interactive terminal-based portfolio with themed UI',
                        tags: ['Vanilla JS', 'CSS3', 'Canvas']
                    }
                ],
                blog: [
                    {
                        id: 'blog-000',
                        date: '2025-01-01',
                        title: 'Error Loading Reports',
                        excerpt: 'Could not fetch data from schema.json. Displaying fallback content.',
                        tags: ['Error']
                    }
                ],
                bio: 'Senior Frontend & Systems Engineer specializing in immersive web experiences.',
                skills: ['JavaScript', 'Python', 'Systems Design', 'Security'],
                contact: {
                    email: 'your@email.com',
                    github: 'https://github.com/yourusername',
                    linkedin: 'https://linkedin.com/in/yourusername'
                }
            },
            terminal: {
                vfs: {
                    '/': { 'type': 'dir', 'children': ['error.txt'] },
                    '/error.txt': { 'type': 'file', 'content': 'Error: terminal.json failed to load.' }
                },
                help: 'Help file not found.',
                bootLogs: ['[ERROR] Boot logs not found.'],
                fortunes: ['Error loading fortunes.']
            }
        };
    }

    /**
     * Get all data (for debugging)
     * @returns {Object} - Full data object
     */
    getAllData() {
        return {
            gui: this.data,
            terminal: this.terminalData
        };
    }

    /**
     * Searches all projects and blog posts for a matching tag.
     * @param {string} tag - The skill name to filter by (e.g., "AWS")
     * @returns {object} - An object { projects: [], blogs: [] }
     */
    getContentByTag(tag) {
        // Get all content from the *manifest*
        const projects = this.getProjects();
        const blogs = this.getBlogPosts();

        const lowerCaseTag = tag.toLowerCase();

        // Filter projects
        const relatedProjects = projects.filter(project => 
            project.tags && project.tags.some(t => t.toLowerCase() === lowerCaseTag)
        );

        // Filter blog posts
        const relatedBlogs = blogs.filter(blog =>
            blog.tags && blog.tags.some(t => t.toLowerCase() === lowerCaseTag)
        );

        return { 
            projects: relatedProjects, 
            blogs: relatedBlogs 
        };
    }

    /**
     * Retrieves the manual page for a specific command.
     * @param {string} commandName
     * @returns {object | null} The man page object or null.
     */
    getManPage(commandName) {
        // ✅ FIX: Check this.terminalData, not this.content
        if (!this.isLoaded || !this.terminalData?.man_pages) {
            console.error('[ContentManager] Man pages not loaded.');
            return null;
        }
        return this.terminalData.man_pages[commandName] || null;
    }
}

// ✅ Create singleton instance
const ContentManagerInstance = new ContentManager();

// ✅ Export singleton
export default ContentManagerInstance;